import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { debounceTime, map, Observable, shareReplay } from 'rxjs';
import { AdminClinicService } from 'src/app/components/clinics/services/admin-clinic.service';
import { ClinicTypeInterface } from 'src/app/shared/models/clinic-type.interface';
import { ClinicInterface } from 'src/app/shared/models/clinic.interface';
import { ClinicTypeService } from 'src/app/shared/services/clinic-type.service';

@Component({
  selector: 'app-scc-clinic',
  templateUrl: './scc-clinic.component.html',
  styleUrls: ['./scc-clinic.component.scss']
})
export class SccClinicComponent implements OnInit {
  @Input() clinicFormGroup: FormGroup = new FormGroup({});
  @Output() clinicSelected = new EventEmitter<ClinicInterface>();

  clinicLabelAlreadyExistWarning = false;
  clinicLabelAlreadyExistConfirm = false;

  clinicTypes$: Observable<ClinicTypeInterface[]>;

  clinicList: ClinicInterface[] = [];

  addNewClinic = false;

  constructor(
    private readonly adminClinicTypeService: ClinicTypeService,
    private readonly adminClinicService: AdminClinicService,
    private readonly formBuilder: FormBuilder
  ) { }

  ngOnInit(): void {
    this.clinicFormGroup.addControl('name', this.formBuilder.control('', Validators.required));
    this.clinicFormGroup.addControl('zipCode', this.formBuilder.control('', [Validators.required, Validators.pattern('[0-9]{5}')]));

    this.clinicFormGroup.get('name')?.disable();

    this.clinicFormGroup.get('zipCode')?.statusChanges.subscribe(status => {
      if (status === 'VALID') {
        this.clinicFormGroup.get('name')?.enable();
      } else {
        this.clinicFormGroup.get('name')?.disable();
        this.clinicFormGroup.get('name')?.setValue('');
      }
    });

    // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
    const clinicAlreadyExist: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => this.adminClinicService.searchClinicsWithLevenshtein(control.value as string, this.clinicFormGroup.get('zipCode')?.value as string).pipe(
      debounceTime(250),
      map((res: ClinicInterface[]) => {
        this.clinicList = res;

        // Distance >= 0.7 < 0.8 -> Warning
        const testWarning = res.find(clinic => (clinic?.distance ?? 0) >= 0.7);
        this.clinicLabelAlreadyExistWarning = (testWarning?.distance ?? 1) < 0.8;

        // Distance >= 0.8 < 1 -> Confirm
        const testConfirm = res.find(clinic => (clinic?.distance ?? 0) >= 0.8);
        this.clinicLabelAlreadyExistConfirm = (testConfirm?.distance ?? 1) < 1;

        if (this.clinicLabelAlreadyExistWarning || this.clinicLabelAlreadyExistConfirm) {
          return null;
        }

        // Distance == 1 -> Error
        const testError = res.find(clinic => (clinic?.distance ?? 0) >= 1);
        return testError ? { alreadyExist: true } : null;
      })
    );

    this.clinicFormGroup.get('name')?.setAsyncValidators(clinicAlreadyExist);
    this.clinicTypes$ = this.adminClinicTypeService.getClinicTypes().pipe(shareReplay(1));
  }

  onClinicSelected(event: { clinic?: ClinicInterface | undefined; name: string; zipCode: string }) {
    if (!event) {
      const postalCode = this.clinicFormGroup.get('zipCode')?.value;
      this.clinicFormGroup.reset();
      this.clinicFormGroup.get('zipCode')?.setValue(postalCode);
      this.clinicSelected.emit();
      return;
    }

    this.clinicFormGroup.get('name')?.setValue(event.name);
    this.clinicFormGroup.get('zipCode')?.setValue(event.zipCode);

    if (!event.clinic) {
      this.addNewClinic = true;
      this.clinicFormGroup.addControl('clinicType', this.formBuilder.control<number | undefined>(undefined, Validators.required));
      this.clinicFormGroup.addControl('address1', this.formBuilder.control('', Validators.required));
      this.clinicFormGroup.addControl('address2', this.formBuilder.control(''));
      this.clinicFormGroup.addControl('city', this.formBuilder.control('', Validators.required));
      this.clinicFormGroup.addControl('phone', this.formBuilder.control('', Validators.required));
      this.clinicFormGroup.addControl('email', this.formBuilder.control('', [Validators.required, Validators.email]));
      return;
    }

    this.clinicSelected.emit(event.clinic);
  }
}
